/// <reference types="resize-observer-browser" />
import React, { useRef, FC, useLayoutEffect, useCallback, useEffect, RefObject } from 'react';
import { CartProps, Cart } from '../Cart';
import { Footer, FooterProps } from '../Footer';
import { isInViewport, useScrollDirection } from '../util';
import { SCROLL_DIRECTION } from '../constants';
import { isNull, find } from 'lodash';
import { EVENTS } from '@trip-ux/constants';

export interface StickyCartProps extends CartProps {
  footer: FooterProps;
  className?: string;
}

export enum CART_POSITION_CLASSES {
  RELATIVE = 'sticky-position-relative',
  FIXED = 'sticky-position-fixed'
}

export const toggleClass = (ele: RefObject<HTMLElement>, setFixed: boolean): void => {
  if (setFixed && !ele.current.classList.contains(CART_POSITION_CLASSES.FIXED)) {
    ele.current.classList.add(CART_POSITION_CLASSES.FIXED);
    ele.current.classList.remove(CART_POSITION_CLASSES.RELATIVE);
  } else if (!setFixed && !ele.current.classList.contains(CART_POSITION_CLASSES.RELATIVE)) {
    ele.current.classList.add(CART_POSITION_CLASSES.RELATIVE);
    ele.current.classList.remove(CART_POSITION_CLASSES.FIXED);
  }
};

export const StickyCart: FC<StickyCartProps> = ({
  footer,
  className = '',
  viewChangeComponent,
  applyChangeComponent
}) => {
  const cartRef = useRef<HTMLDivElement>();
  const footerRef = useRef<HTMLDivElement>();
  const stickyRef = useRef<HTMLDivElement>();
  const direction = useScrollDirection();

  useLayoutEffect(() => {
    if (!isInViewport(footerRef)) {
      toggleClass(footerRef, false);
      toggleClass(cartRef, true);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const inViewPort = isInViewport(footerRef);
    if (
      !isNull(inViewPort) &&
      isInViewport(footerRef, -cartRef.current.clientHeight) &&
      direction === SCROLL_DIRECTION.DOWN
    ) {
      toggleClass(cartRef, false);
    } else if (!isNull(inViewPort) && !inViewPort && direction === SCROLL_DIRECTION.UP) {
      toggleClass(cartRef, true);
    }
  }, [direction, cartRef, footerRef]);

  useEffect(() => {
    window.addEventListener(EVENTS.SCROLL, handleScroll, true);
    return () => {
      window.removeEventListener(EVENTS.SCROLL, handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      const body = find(entries, entry => entry.target.localName === 'body');
      setTimeout(() => {
        const inViewPort = isInViewport(footerRef);
        if (
          !inViewPort &&
          footerRef.current &&
          body.contentRect.height > window.innerHeight + footerRef.current.clientHeight
        ) {
          toggleClass(footerRef, false);
          toggleClass(cartRef, true);
        } else if (
          footerRef.current &&
          cartRef.current &&
          body.contentRect.height < window.innerHeight + footerRef.current.clientHeight - cartRef.current.clientHeight
        ) {
          toggleClass(cartRef, false);
        }
      }, 0);
    });
    observer.observe(document.body);
    return () => {
      observer.unobserve(document.body);
    };
  }, []);

  return (
    <footer className={className} ref={stickyRef}>
      <Cart
        ref={cartRef}
        className="cart-float"
        viewChangeComponent={viewChangeComponent}
        applyChangeComponent={applyChangeComponent}
      />
      <Footer ref={footerRef} {...footer} />
    </footer>
  );
};

StickyCart.displayName = 'StickyCart';
export default StickyCart;
